/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.driver.worker.WebWorkerDriver
import com.wiz3r.database.Wiz3rDatabase
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.Worker

/**
 * JavaScript/Web implementation of DatabaseRepositoryFactory
 * Uses Web Worker driver for browser-based SQLite storage
 */
actual class DatabaseRepositoryFactory {
    private var database: Wiz3rDatabase? = null
    private val databaseReady = CompletableDeferred<Wiz3rDatabase>()

    actual constructor() {
        // Default constructor for JS
    }

    actual fun create(databaseName: String): DatabaseRepository {
        val driver: SqlDriver = try {
            // Create WebWorkerDriver with database name for persistence
            console.log("🔧 Creating WebWorkerDriver with database: $databaseName")
            WebWorkerDriver(
                Worker(
                    js("""new URL("@cashapp/sqldelight-sqljs-worker/sqljs.worker.js", import.meta.url)""")
                )
            ).also { driver ->
                // Initialize schema - SQLDelight's create() is idempotent with IndexedDB
                // It only creates tables if they don't exist, preserving existing data
                GlobalScope.launch {
                    try {
                        Wiz3rDatabase.Schema.create(driver).await()
                        runMigrations(driver)
                        database = Wiz3rDatabase(driver)
                        databaseReady.complete(database!!)
                        console.log("✅ Database '$databaseName' ready (IndexedDB persistence enabled)")
                        console.log("💾 Data persists across page reloads")
                    } catch (e: Exception) {
                        console.error("❌ Failed to initialize database: ${e.message}")
                        databaseReady.completeExceptionally(e)
                    }
                }
            }
        } catch (e: Exception) {
            throw Exception("Failed to create WebWorker driver: ${e.message}")
        }

        return DatabaseRepositoryStub()
    }

    /**
     * Runtime ALTER TABLE migration for columns added to existing tables.
     *
     * `Schema.create()` only creates tables that don't exist — for users with
     * a persistent IndexedDB database, columns added later to `CREATE TABLE IF
     * NOT EXISTS` blocks in schema.sq are silently missing, causing writes to
     * those columns to be discarded.
     *
     * We swallow "duplicate column" errors so this is safe to run every load.
     */
    private suspend fun runMigrations(driver: SqlDriver) {
        suspend fun tryAlter(sql: String) {
            try {
                driver.execute(null, sql, 0).await()
            } catch (e: Throwable) {
                // SQLite reports "duplicate column name" when the column
                // already exists. Any other failure is logged but non-fatal —
                // we don't want a bad migration to block the entire app.
                val msg = e.message ?: ""
                if (!msg.contains("duplicate column", ignoreCase = true)) {
                    console.warn("⚠️ Migration skipped: $sql — ${e.message}")
                }
            }
        }
        // Wiz 3R per-goal debt fields. Two columns added in a later commit
        // after the original CREATE TABLE shipped.
        tryAlter("ALTER TABLE savings_goals ADD COLUMN debt_payment_frequency TEXT")
        tryAlter("ALTER TABLE savings_goals ADD COLUMN debt_monthly_equivalent_payment REAL")
        // Password reset token columns — server-only writers, but SELECT * on
        // users would fail on a pre-existing client DB without these columns.
        tryAlter("ALTER TABLE users ADD COLUMN password_reset_token_hash TEXT")
        tryAlter("ALTER TABLE users ADD COLUMN password_reset_expires_at TEXT")
    }

    suspend fun getDatabaseAsync(): Wiz3rDatabase {
        return databaseReady.await()
    }

    fun getDatabase(): Wiz3rDatabase {
        return database ?: throw IllegalStateException("Database not initialized. Call create() first.")
    }

    fun isReady(): Boolean = databaseReady.isCompleted
}

/**
 * Web-specific helper for managing database instance
 * Uses singleton pattern for browser environment
 */
object WebDatabaseRepositoryFactory {
    private var FACTORY_INSTANCE: DatabaseRepositoryFactory? = null

    private fun getFactory(databaseName: String = "wiz3r.db"): DatabaseRepositoryFactory {
        return FACTORY_INSTANCE ?: run {
            val factory = DatabaseRepositoryFactory()
            factory.create(databaseName)
            FACTORY_INSTANCE = factory
            factory
        }
    }

    suspend fun getDatabaseAsync(databaseName: String = "wiz3r.db"): Wiz3rDatabase {
        val factory = getFactory(databaseName)
        return factory.getDatabaseAsync()
    }
}